import "../styles/events.css";
import { Endgame, LevelCatalog } from "@highmarch/app";
import { LevelModifiers, LiveEventData, eventEndsAt, eventStartsAt, trialEndsAt } from "@highmarch/domain";
import { earnHtml, eventArtHtml, quarryName, themeClass } from "./eventLook";
import { barHtml, btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { paidContents, rewardChest } from "../chestReveal";
import { toast } from "../toast";
import { emberGlyph, marksGlyph, paidLine, trophyTagHtml } from "./eventRewards";
import { flyReward } from "./ordersSheet";
import { econCue } from "./purchaseFx";
import { SHOP_SKIN } from "../shopSkin";
import { checkHtml, sheetHeadHtml } from "../components";
import { bindRewardFocus, evBannerHtml, evChipHtml, rewardRowHtml, rewardTileHtml } from "./evKit";

/** What the Events sheet needs: progress, the endgame use cases, the clock and a way to start a modified run. */
export interface EventsSheetCtx {
  readonly catalog: LevelCatalog;
  readonly endgame: Endgame;
  readonly now: () => number;
  /** Saves and redraws the hub (pills, the More badge). */
  readonly onChange: () => void;
  /** Starts a Heroic or Trial run (the Events sheet goes with the hub). */
  readonly startRun: (level: number, modifiers: LevelModifiers) => void;
  /** Panels features add under the live event (MP-21's event modes: features/registry.ts EventPanel). */
  readonly panels?: readonly SheetPanel[];
}

/** A feature's panel under the live event (features/registry.ts EventPanel, structurally). */
export interface SheetPanel {
  readonly id: string;
  html(e: LiveEventData): string;
  bind?(panel: HTMLElement, api: { close(): void; rerender(): void }): void;
}

const SECONDS_PER_HOUR = 3600, SECONDS_PER_DAY = 86_400;
/** Heroic levels offered as chips on the card (the rest wait on the campaign map). */
const HEROIC_CHIPS = 4;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** "3d 4h", "5h", "12m": how long until `seconds` from now. */
export function fmtLeft(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / SECONDS_PER_DAY), h = Math.floor((s % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h` : `${Math.max(1, Math.ceil(s / 60))}m`;
}

/** "24 Oct": a UTC day number as a short date. */
export function fmtDay(day: number): string {
  const d = new Date(day * SECONDS_PER_DAY * 1000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** The live event's banner, its embers bar and free track, the mode panels, and the ways to earn. */
function liveHtml(ctx: EventsSheetCtx, e: LiveEventData, panels = ""): string {
  const g = ctx.endgame, now = ctx.now(), have = g.progress.embersOf(e.id);
  const nodes = e.nodes.map((n, i) => {
    const claimed = g.progress.isClaimed(e.id, i), ready = !claimed && have >= n.embers && g.open;
    const state = claimed ? "claimed" : ready ? "ready" : "is-locked";
    const foot = claimed ? `<span class="evfoot got">${checkHtml()}</span>`
      : ready ? btnHtml("CLAIM", "gold", { className: "claim evfoot", data: `data-node="${i}"` })
      : `<span class="evfoot">${emberGlyph(14)}${fmtCompact(n.embers)}</span>`;
    return rewardTileHtml(n.reward, { top: String(i + 1), foot, dim: state === "is-locked", dot: ready, className: `evnode ${state}${n.reward.emblem ? " grand" : ""}`, data: `data-i="${i}"` });
  }).join("");
  const next = e.nodes.find((n) => n.embers > have), prev = [...e.nodes].reverse().find((n) => n.embers <= have)?.embers ?? 0;
  const lock = g.open ? "" : `<p class="evlock">${icon("lock", 14)} Clear level ${g.unlockAfterLevel} to start</p>`;
  const quarry = quarryName(ctx.catalog.balance, e);
  const chips = `${evChipHtml(`${icon("clock", 13)}${fmtLeft(eventEndsAt(e) - now)}`)}${evChipHtml(`${emberGlyph(16)}<b>${fmtCompact(have)}</b>`, "embers")}${quarry ? evChipHtml(`${icon("target", 13)}${quarry}`) : ""}`;
  const frac = next ? (have - prev) / Math.max(1, next.embers - prev) : 1;
  return `<section class="evlive">
    ${evBannerHtml({ scene: "event", title: e.name, chips, art: eventArtHtml(e, 46), className: themeClass(e) })}
    ${lock}
    <div class="evprog">${emberGlyph(20)}${barHtml(Math.min(1, frac), "gold")}<small class="disp">${next ? `${fmtCompact(have)} / ${fmtCompact(next.embers)}` : "Complete!"}</small></div>
    <div class="evtrack">${nodes}</div>
    ${panels}
    ${earnHtml(ctx.catalog.balance, e)}
  </section>`;
}

function upcomingHtml(e: LiveEventData, now: number): string {
  return `<section class="evsoon">${evBannerHtml({ scene: "event", title: e.name, art: eventArtHtml(e, 46), className: `${themeClass(e)} dim`,
    chips: `${evChipHtml(`${icon("clock", 13)}in ${fmtLeft(eventStartsAt(e) - now)}`)}${evChipHtml(`${fmtDay(e.startDay)} – ${fmtDay(e.endDay - 1)}`)}` })}</section>`;
}

function trialHtml(ctx: EventsSheetCtx): string {
  const g = ctx.endgame, now = ctx.now(), B = ctx.catalog.balance;
  const t = g.trial(now);
  if (!t) return `<section class="evcard trial locked"><div class="evhead"><span class="badge disp">WEEKLY TRIAL</span><small>${icon("lock", 12)} level ${B.events.trial.unlockAfterLevel}</small></div></section>`;
  const won = g.trials.wonThisWeek();
  return `<section class="evcard trial${won ? " won" : ""}">
    <div class="evhead"><span class="badge disp">WEEKLY TRIAL</span><small>${icon("clock", 12)} ${fmtLeft(trialEndsAt(t.week) - now)}</small></div>
    <div class="evbody"><div class="disc disp">${t.level}</div><div class="txt"><b>${B.level(t.level).name}</b><button type="button" class="mod twist" data-blurb="${t.modifier.blurb.replace(/"/g, "&quot;")}">${icon("bolt", 13)} ${t.modifier.name}</button></div>
      ${btnHtml(won ? "AGAIN" : "PLAY", won ? "dark" : "gold", { icon: "play", className: "disp go trialgo" })}</div>
    ${won ? `<p class="done">${checkHtml()} Won this week</p>` : rewardRowHtml(B.events.trial.reward)}
  </section>`;
}

function heroicHtml(ctx: EventsSheetCtx): string {
  const g = ctx.endgame;
  const waiting = g.heroicWaiting(), have = g.trials.heroicCount;
  const chips = waiting.slice(0, HEROIC_CHIPS).map((l) => `<button class="hchip" data-level="${l}"><b class="disp">${l}</b><small>${marksGlyph(12)}+${fmtCompact(g.heroicMarks(l))}</small></button>`).join("");
  const hp = Math.round((ctx.catalog.balance.events.heroic.enemyHpMult.toNumber() - 1) * 100);
  const body = waiting.length > 0
    ? `<div class="hchips">${chips}</div>`
    : `<p class="hnone">${icon("star", 14)}${icon("star", 14)}${icon("star", 14)} a level to open it</p>`;
  return `<section class="evcard heroic"><div class="evhead"><span class="badge disp">HEROIC</span><small>${icon("heart", 12)} +${hp}% raider health · ${icon("flame", 12)} ${have}${g.heroicTrophies > 0 && waiting.length > 0 ? ` · ${trophyTagHtml(g.heroicTrophies, 13, true)}` : ""}</small></div>${body}</section>`;
}

/**
 * More → Events (FINISH_PLAN WP6): the live event's banner and free track (or the next event's date), the Weekly
 * Trial and the Heroic tier. Claims pay at once (a chest plays the reveal over the sheet); PLAY starts the run.
 */
export function showEvents(parent: HTMLElement, ctx: EventsSheetCtx): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = `sheet panel events ${SHOP_SKIN}`;
  parent.append(sheet);
  const render = (): void => {
    const now = ctx.now(), g = ctx.endgame;
    g.syncHub(now);
    const live = g.live(now), soon = live ? null : g.upcoming(now);
    const sub = live || soon ? undefined : "Trials and Heroic";
    // MP-21: a mode week's own panel (Boss Week's tiers, Race Week's medals, the Odyssey's maps) sits under its track
    const panels = live && g.open ? (ctx.panels ?? []).map((p) => ({ p, html: p.html(live) })).filter((x) => x.html !== "") : [];
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Events", sub, portrait: live ? eventArtHtml(live, 48) : emberGlyph(48), tint: "ember" })}
      ${live ? liveHtml(ctx, live, panels.map((x) => `<div class="evpanel" data-panel="${x.p.id}">${x.html}</div>`).join("")) : soon ? upcomingHtml(soon, now) : ""}
      ${trialHtml(ctx)}
      ${heroicHtml(ctx)}`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    for (const x of panels) { const el = sheet.querySelector<HTMLElement>(`.evpanel[data-panel="${x.p.id}"]`); if (el) x.p.bind?.(el, { close: () => sheet.remove(), rerender: render }); }
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".evnode .claim")) b.onclick = () => claim(b, Number(b.dataset.node));
    bindRewardFocus(sheet, parent, ctx.catalog.balance, ctx.catalog.highestLevelCleared);
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".twist")) b.onclick = () => toast(parent, b.dataset.blurb ?? "", 2600);
    sheet.querySelector<HTMLButtonElement>(".trialgo")?.addEventListener("click", () => {
      const t = g.trial(ctx.now());
      if (t) { sheet.remove(); ctx.startRun(t.level, g.trialRun(t)); }
    });
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".hchip")) b.onclick = () => { sheet.remove(); ctx.startRun(Number(b.dataset.level), g.heroicRun()); };
    // the track opens on the first reward to claim, else the next one to reach
    const track = sheet.querySelector<HTMLElement>(".evtrack");
    const focus = track?.querySelector<HTMLElement>(".evnode.ready") ?? track?.querySelector<HTMLElement>(".evnode.is-locked");
    if (track && focus) track.scrollLeft = Math.max(0, focus.offsetLeft - track.clientWidth / 2 + focus.clientWidth / 2);
  };
  const claim = (btn: HTMLElement, node: number): void => {
    const c = ctx.endgame.claimNode(ctx.now(), node);
    if (!c) return;
    econCue("event");
    ctx.onChange();
    flyReward(btn, c.paid.gold);
    const line = `${paidLine(c.paid, c.gear)}${c.eventMarks > 0 ? ` · +${fmtCompact(c.eventMarks)} Marks` : ""}`;   // the Event shop's Marks (docs/SHOP.md §9.3)
    if (c.paid.chest) {
      const chest = c.paid.chest;
      rewardChest(sheet.parentElement ?? sheet, ctx.catalog.balance, chest.rarity, paidContents(c.paid, c.gear), line, render);   // M30 #10
      return;                                           // the track redraws when the reveal closes (it sits on the sheet)
    }
    toast(parent, line, 2200);
    render();
  };
  render();
  return sheet;
}
